import { Component, Inject, OnInit, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, Validators } from '@angular/forms';
import {
  RENAME_SURVEY_EMPTY_ERROR,
  RENAME_SURVEY_DUPLICATE_ERROR,
} from '../constants';
import { SurveyOverviewService } from '../../core/services/survey-overview.service';
import { surveyNameValidator } from '../validators/survey-name.validator';

export interface RenameSurveyDialogData {
  currentName: string;
  primaryCompanyId: string;
}

@Component({
  selector: 'app-rename-survey-dialog',
  templateUrl: './rename-survey-dialog.component.html',
  styleUrl: './rename-survey-dialog.component.css',
})
export class RenameSurveyDialogComponent implements OnInit {
  nameControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(80),
    surveyNameValidator(),
  ]);
  isCheckingDuplicate = false;

  private duplicateCheckTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public dialogRef: MatDialogRef<RenameSurveyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RenameSurveyDialogData,
    private surveyOverviewService: SurveyOverviewService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.nameControl.setValue(this.data.currentName);
  }

  onNameChange(): void {
    const trimmedName = (this.nameControl.value || '').trim();
    if (!trimmedName) {
      this.isCheckingDuplicate = false;
      this.cancelPendingCheck();
      return;
    }
    if (trimmedName === this.data.currentName) {
      this.isCheckingDuplicate = false;
      this.cancelPendingCheck();
      // Clear any duplicate error if reverted to original
      if (this.nameControl.hasError('nameExists')) {
        this.nameControl.setErrors(this.removeError('nameExists'));
      }
      return;
    }
    // Clear previous duplicate error before re-checking
    if (this.nameControl.hasError('nameExists')) {
      this.nameControl.setErrors(this.removeError('nameExists'));
    }
    this.checkDuplicateName(trimmedName);
  }

  private cancelPendingCheck(): void {
    if (this.duplicateCheckTimer) {
      clearTimeout(this.duplicateCheckTimer);
      this.duplicateCheckTimer = null;
    }
  }

  private checkDuplicateName(name: string): void {
    this.cancelPendingCheck();
    this.isCheckingDuplicate = true;
    this.duplicateCheckTimer = setTimeout(() => {
      this.performDuplicateCheck(name);
    }, 400);
  }

  private performDuplicateCheck(name: string): void {
    this.surveyOverviewService
      .getSurveyName(this.data.primaryCompanyId, name)
      .then((existing) => {
        const currentTrimmed = (this.nameControl.value || '').trim();
        if (currentTrimmed === name) {
          this.ngZone.run(() => {
            if (existing) {
              this.nameControl.setErrors({
                ...this.nameControl.errors,
                nameExists: true,
              });
            } else if (this.nameControl.hasError('nameExists')) {
              this.nameControl.setErrors(this.removeError('nameExists'));
            }
            this.isCheckingDuplicate = false;
          });
        }
      })
      .catch(() => {
        const currentTrimmed = (this.nameControl.value || '').trim();
        if (currentTrimmed === name) {
          this.ngZone.run(() => {
            if (this.nameControl.hasError('nameExists')) {
              this.nameControl.setErrors(this.removeError('nameExists'));
            }
            this.isCheckingDuplicate = false;
          });
        }
      });
  }

  async onSave(): Promise<void> {
    const trimmedName = (this.nameControl.value || '').trim();
    if (!trimmedName || this.nameControl.invalid || this.isCheckingDuplicate) {
      return;
    }
    this.dialogRef.close(trimmedName);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  get isSaveDisabled(): boolean {
    const trimmedName = (this.nameControl.value || '').trim();
    return (
      !trimmedName ||
      trimmedName === this.data.currentName ||
      this.nameControl.invalid ||
      this.isCheckingDuplicate
    );
  }

  get validationError(): string {
    const trimmedValue = (this.nameControl.value || '').trim();
    if (
      (!trimmedValue && this.nameControl.dirty) ||
      (this.nameControl.hasError('required') && this.nameControl.dirty)
    ) {
      return RENAME_SURVEY_EMPTY_ERROR;
    }
    if (this.nameControl.hasError('maxlength')) {
      return 'Survey name cannot exceed 80 characters';
    }
    if (this.nameControl.hasError('invalidFilenameChars')) {
      return this.nameControl.getError('invalidFilenameChars').message;
    }
    if (this.nameControl.hasError('nameExists')) {
      return RENAME_SURVEY_DUPLICATE_ERROR;
    }
    return '';
  }

  private removeError(errorKey: string): Record<string, any> | null {
    if (!this.nameControl.errors) return null;
    const { [errorKey]: _, ...remaining } = this.nameControl.errors;
    return Object.keys(remaining).length > 0 ? remaining : null;
  }
}
